import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  Inject,
} from '@angular/core';
import moment, { Moment } from 'moment';
import { IDatePickerConfig, ISelectionEvent } from 'ng2-date-picker';
import { Logger } from '@utils/logger';
import clone from 'lodash/clone';
import { momentMonthYearFormat } from '@utils/il8n/moment-il8n';
import { TOpens } from 'ng2-date-picker/common/types/poistions.type';
import { DOCUMENT } from '@angular/common';

const logger = Logger.getLogger('ft-date-picker');

enum DatePickerType {
  MONTH = 'month',
  DAY = 'day',
}

@Component({
  selector: 'ft-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
})
export class DatePickerComponent implements OnInit, OnChanges {
  constructor(@Inject(DOCUMENT) readonly documentRef: Document) {
    if (!DatePickerComponent.initialized) {
      DatePickerComponent.initialize();
    }
  }
  static initialized = false;
  static config: IDatePickerConfig = {
    firstDayOfWeek: 'su',
    monthFormat: 'MMM, YYYY',
    disableKeypress: true,
    allowMultiSelect: false,
    closeOnSelect: undefined,
    closeOnSelectDelay: 100,
    onOpenDelay: 0,
    weekDayFormat: 'ddd',
    appendTo: document.body,
    drops: 'down',
    opens: 'left',
    showNearMonthDays: true,
    showWeekNumbers: false,
    enableMonthSelector: true,
    format: 'L',
    yearFormat: 'YYYY',
    showGoToCurrent: true,
    dayBtnFormat: 'DD',
    monthBtnFormat: 'MMM',
    hours12Format: 'hh',
    hours24Format: 'HH',
    meridiemFormat: 'A',
    minutesFormat: 'mm',
    minutesInterval: 1,
    secondsFormat: 'ss',
    secondsInterval: 1,
    showSeconds: false,
    showTwentyFourHours: true,
    timeSeparator: ':',
    multipleYearsNavigateBy: 10,
    showMultipleYearsNavigation: false,
    numOfMonthRows: 4,
  };
  @Input() type = DatePickerType.DAY;
  @Input() value: Moment | string;
  @Input() min: Moment | string;
  @Input() max: Moment | string;
  @Input() label: string;
  @Input() opens: TOpens;
  @Input() isWeekEnding: boolean;
  @Input() placeholder: string;

  displayDate: Moment;
  minDate: Moment;
  maxDate: Moment;
  config: IDatePickerConfig;
  selectedDate = moment();
  @Output() selected = new EventEmitter();

  static initialize() {
    // initialize from moment configuration - is this required or will it just work?
    DatePickerComponent.config.locale = moment.locale();
    DatePickerComponent.initialized = true;
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.init();
  }

  private init() {
    this.config = clone(DatePickerComponent.config);
    this.config.max = this.max ? moment(this.max) : undefined;
    this.config.min = this.min ? moment(this.min) : undefined;
    if (this.type === DatePickerType.MONTH) {
      this.config.format = momentMonthYearFormat();
    }
    if (this.opens) {
      this.config.opens = this.opens; // Set opening direction (left or right) from the parent
    }
    // For "what happened last week" gray out/disable the dates besides Friday
    if (this.isWeekEnding) {
      this.config.isDayDisabledCallback = (momentDate: Moment) =>
        moment(momentDate).format('dddd') !== 'Friday' ||
        moment(momentDate).isAfter(moment(this.max));
    }
    this.displayDate = moment(this.value);
    this.selectedDate = this.displayDate;
  }

  dateSelected(event: ISelectionEvent) {
    this.selected.emit(event.date);
  }

  onCalendarDraw() {
    if (this.isWeekEnding) {
      setTimeout(() => {
        this.setToolTip();
      }, 50); // wait for calendar to finish drawing dates
    }
  }

  // Set Tooltip on hover on Buttons for Fridays
  private setToolTip() {
    const fridayButtons: NodeListOf<Element> = this.documentRef
      .querySelector('.dp-day-calendar-container')
      .querySelectorAll('.dp-calendar-day:not([disabled])');
    fridayButtons.forEach((el: HTMLElement) => {
      el.title = 'Select week ending'; // Tooltip for button
    });
  }
}
